import styled from "@emotion/styled";
import { AiChat, useAsStreamAdapter, ComposerOptions } from '@nlux/react';
import { useState, useEffect } from "react";
import '@nlux/themes/nova.css';
import { Dict } from 'mixpanel-browser';
import { List } from "../../list/store";

import { sendFactory } from './send';
import { personas } from './personas';

import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { State } from "../../utils/store";
import { Link } from "react-router-dom";
import { useParams } from "react-router";
import { Segment } from "../../utils/Segment";

const TrackedAiChat = ({ 
  conversationStarters, 
  companyUuid, 
  selectedCompany, 
  selectedThread, 
  selectedUser, 
  config,
  ...props 
}: any) => {
  const handleConversationStarterClick = (prompt: string, label: string) => {
    Segment.analytics?.track("scott-conversation-starter", {
      prompt,
      label,
      companyUuid,
      companyName: selectedCompany?.name,
      threadUuid: selectedThread?.thread_uuid,
      userUuid: selectedUser?.uuid,
      clientUuid: config.client_uuid
    });
  };

  const trackedConversationStarters = conversationStarters.map((starter: any) => ({
    ...starter,
    onClick: () => handleConversationStarterClick(starter.prompt, starter.label)
  }));

  return (
    <AiChat
      {...props}
      conversationOptions={{
        ...props.conversationOptions,
        conversationStarters: trackedConversationStarters
      }}
    />
  );
};

export const ChatBot = (config: Dict) => {
  const adapter = useAsStreamAdapter(sendFactory(config), [config]);
  const dispatch = useAppDispatch();

  const { clientSlug } = useParams<{ clientSlug: string }>();

  const selectedUser = useAppSelector(
    (state: State) => state.auth.user
  );

  const selectedThread = useAppSelector(
    (state: State) => state.scottThreadList.selectedThread
  );

  const { selectedCompany } = useAppSelector(
    (state: State) => state.goldenBasket
  );
  const { companyUuid } = useParams<{ companyUuid: string }>();

  const composerOptions: ComposerOptions = {
    autoFocus: true,
    disableSubmitButton: false,
    placeholder: "Ask Scott anything about innovative companies you are searching for...",
  };

  const initialConversation: any[] = selectedThread?.initial_conversation || [];
  const sortedInitialConversation: any[] = sortAndRemoveTimestamp(initialConversation);

  const conversationStarters = [
    {
      icon: <span>🏢</span>,
      label: `Tell me more ...`,
      prompt: `Tell me about ${selectedCompany?.name}, ${selectedCompany?.url}`
    },
    {
      icon: <span>🏢</span>,
      label: `Who are their competitors`,
      prompt: `Who are the competitors of ${selectedCompany?.name}, ${selectedCompany?.url}`
    },
    {
      icon: <span>🏢</span>,
      label: `What's their business model`,
      prompt: `What is the business model of ${selectedCompany?.name}, ${selectedCompany?.url}`
    }
  ];

  return (
    <Wrapper>
      <Container>
      {companyUuid && selectedCompany?.uuid === companyUuid ? (
          <TrackedAiChat
            key={selectedThread?.thread_uuid}
            adapter={adapter}
            personaOptions={personas(`How can I help you research ${selectedCompany?.name}`)}
            displayOptions={{
              themeId: 'custom-nova',
            }}
            conversationStarters={conversationStarters}
            companyUuid={companyUuid}
            selectedCompany={selectedCompany}
            selectedThread={selectedThread}
            selectedUser={selectedUser}
            config={config}
            className="nlux-chatbot"
          />
        ) : sortedInitialConversation.length === 0 ? (
          <AiChat
            key={selectedThread?.thread_uuid}
            adapter={adapter}
            personaOptions={personas('Your AI scouting assistant')}
            displayOptions={{ 
              themeId: 'custom-nova',
            }}
            composerOptions={composerOptions}
          />
        ) : (
          <AiChat
            key={selectedThread?.thread_uuid}
            adapter={adapter}
            personaOptions={personas('Your AI scouting assistant')}
            displayOptions={{ 
              themeId: 'custom-nova',
            }}
            composerOptions={composerOptions}
            initialConversation={sortedInitialConversation}
          />
      )}

      </Container>
      <p
        style={{
          textAlign: 'center', 
          color: 'var(--content-color-tertiary)', 
          marginTop: '10px',
        }}
      >
        Keep in mind that Scott can make mistakes. To include Novable Experts, <Link to={`/${clientSlug}/new-campaign`}>create a Campaign</Link>
      </p>
    </Wrapper>
  );
};

type MessageDict = {
  role: string;
  message: string;
  timestamp: string;
};

function sortAndRemoveTimestamp(data: MessageDict[]): Omit<MessageDict, 'timestamp'>[] {
  try {
    const dataCopy = [...data];

    dataCopy.sort((a, b) => {
        const dateA = new Date(a.timestamp);
        const dateB = new Date(b.timestamp);
        return dateA.getTime() - dateB.getTime();
    });

    return dataCopy.map(({ timestamp, ...rest }) => rest);
  } catch (error) {
    console.error(error);
    return data;
  }
}

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 5rem 5rem 1rem 5rem;
  nlux-PromptInput--BackgroundColor: green;
`;

const Container = styled.div`
  height: 100%;
  display: flex;
  background: var(--background-color-secondary);
  flex-direction: column;
  overflow: hidden;
  padding: 0 0 0 0;
`;
